import { ImageScaleView, ImageView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import PopupMatch2BaseFullscreen from '../../minigame/match2-timestep/components/PopupMatch2BaseFullscreen';
import StreakMessage from '../components/StreakMessage';
import gsap from 'gsap';
import {
  getStreakBattleRoyaleCurrentMilestone,
  getStreakBattleRoyaleGoal,
  getStreakBattleRoyaleLastMilestoneSeen,
  getStreakBattleRoyaleStatus,
  isStreakBattleRoyaleEnabled,
  isStreakBattleRoyaleExpired,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { getStreakBattleRoyalePlayersAtMilestone } from '../streakbattleroyale.utils';
import {
  openStreakBattleRoyaleMatchmaking,
  openStreakBattleRoyaleRetry,
  openStreakBattleRoyaleReward,
} from '../streakbattleroyale.sequence';
import { waitFor } from 'src/game/minigame/match2-timestep/lib/util/waitFor';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import StreakTapToContinue from '../components/StreakTapToContinue';
import sounds from 'src/lib/sounds';
import {
  getStreakBattleRoyaleAssetPaths,
  streakBattleRoyaleTheme,
} from '../streakbattleroyale.theme';
import { AssetGroup } from '@play-co/timestep-core';
import { playMatch2 } from 'src/game/minigame/match2-timestep/match2.play';
import { createEmitter } from 'src/lib/Emitter';
import StreakBattleRoyaleInfoPanelV2 from '../components/StreakBattleRoyaleInfoPanelV2';
import StreakClouds from '../components/StreakClouds';
import StreakChest from '../components/StreakChest';
import StreakCloudRow from '../components/StreakCloudRow';
import StreakCloudMascot, {
  streakCloudMascotAssets,
} from '../components/StreakCloudMascot';
import StreakCloudPlatforms from '../components/StreakCloudPlatforms';
import StreakPlayerGroupV2 from '../components/StreakPlayerGroupV2';
import Fireworks from 'src/game/components/Fireworks';
import { streakCloudPlatformAssets } from '../components/StreakCloudPlatform';
import SpineAnimationView from 'src/game/components/shared/SpineAnimationView';
import { spineLoader } from '@play-co/timestep-spine';

const STREAK_BATTLE_ROYALE_ZINDEX = 9997; // Just behind the Match2 pregame popup

const streakBgAssets = {
  loadMethod: spineLoader,
  url: 'assets/ui/match2/streakbattleroyaleV2/spines/bg/spine.mtfuji.bg',
};

const chestOffsetY = -80;

export default class PopupStreakBattleRoyaleV2 extends PopupMatch2BaseFullscreen {
  private close: () => void;
  private info: StreakBattleRoyaleInfoPanelV2;
  private chest: StreakChest;
  private platforms?: StreakCloudPlatforms;
  private players: StreakPlayerGroupV2;
  private shrine: ImageView;
  private cloud?: StreakCloudMascot;
  private message: StreakMessage;
  private youMadeItText: ImageScaleView;
  private firework!: Fireworks;
  private playButton: ButtonScaleViewWithText;
  private current = 0;
  private goal: number;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      bgImageOpts: { clip: true, backgroundColor: '#1F8BAD' },
      zIndex: STREAK_BATTLE_ROYALE_ZINDEX,
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.POPUP,
      },
    });

    this.goal = getStreakBattleRoyaleGoal(user());
    this.close = opts.close;
    const { width, height } = this.getView().style;

    // Background bottom filler - hack fixing black line artefact from background spine animation
    new ImageView({
      superview: this.panel,
      backgroundColor: '#1E8BAD',
      width,
      height: 50,
      y: 1200,
      zIndex: -2,
    });

    this.chest = new StreakChest({
      superview: this.panel,
      x: 472,
      y: 538 + chestOffsetY,
    });

    const backClouds = new StreakClouds(
      this.panel,
      width,
      Math.min(height, 1390),
    );

    this.shrine = new ImageView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('ground'),
      width: 475,
      height: 475,
      x: width - 400,
      y: Math.min(height - 400, 935),
      zIndex: 5,
      canHandleEvents: false,
    });

    this.players = new StreakPlayerGroupV2(this.panel);

    const frontCloud1 = new StreakCloudRow({
      superview: this.panel,
      variant: 'cloud3',
      width,
      y: this.shrine.style.y + 360,
      zIndex: 6,
      gap: 0,
      speed: 1.2,
      canHandleEvents: false,
    });

    const frontCloud2 = new StreakCloudRow({
      superview: this.panel,
      variant: 'cloud3',
      width,
      y: frontCloud1.style.y + 5,
      zIndex: 7,
      offset: 100,
      gap: 0,
      speed: 1.2,
      canHandleEvents: false,
    });

    const whiteFiller = new ImageView({
      superview: this.panel,
      backgroundColor: '#FFFFFF',
      width,
      height: 500,
      y: frontCloud1.style.y + 150,
      zIndex: 8,
      canHandleEvents: false,
    });

    this.message = new StreakMessage({
      superview: this.panel,
      x: width * 0.5,
      y: height - 235,
      zIndex: 101,
    });

    const locale = StateObserver.getLocale();

    this.youMadeItText = new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset(`you_made_it_${locale}`),
      width: locale === 'en' ? 463 : 435,
      height: locale === 'en' ? 86 : 86,
      centerOnOrigin: true,
      x: width * 0.5,
      y: 180,
      zIndex: 102,
      canHandleEvents: false,
    });
    this.hideYouMadeIt(true);

    const ctaWidth = 300;
    const ctaHeight = 92;

    this.playButton = new ButtonScaleViewWithText({
      ...buttons.secondary,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: ctaWidth,
      height: ctaHeight,
      x: width * 0.5,
      y: height - ctaHeight / 2 - 25,
      zIndex: 100,
      superview: this.panel,
      localeText: () => i18n('match2.ui.play'),
      onClick: async () => {
        await playMatch2({ onStart: () => opts.close(), origin: undefined });
      },
      centerOnOrigin: true,
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.POPUP_PLAY,
      },
    });

    // Hide play button by default.
    this.hidePlayButton(true);

    this.info = new StreakBattleRoyaleInfoPanelV2({
      superview: this.panel,
      x: width * 0.5,
      y: 125,
      onClose: () => opts.close(),
    });

    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        backClouds.updateOpts({ height: Math.min(screen.height, 1390) * 1.25 });
        this.shrine.updateOpts({ y: Math.min(screen.height - 400, 935) });
        frontCloud1.updateOpts({ y: this.shrine.style.y + 360 });
        frontCloud2.updateOpts({ y: frontCloud1.style.y + 5 });
        whiteFiller.updateOpts({ y: frontCloud1.style.y + 150 });
        this.playButton.updateOpts({ y: screen.height - ctaHeight / 2 - 25 });
        this.message.updateOpts({ y: screen.height - 235 });
      },
    );
  }

  public init() {
    super.init({});
    const state = user();
    if (!isStreakBattleRoyaleEnabled(state) || !state.streak) {
      throw new Error('Streak event is not available / not initialised');
    }
  }

  private async handleStateUpdate() {
    const state = user();
    const now = StateObserver.now();
    const status = getStreakBattleRoyaleStatus(state);
    const milestone = getStreakBattleRoyaleCurrentMilestone(state, now);
    const expired = isStreakBattleRoyaleExpired(state, now);

    // Show finishing animation and show the reward popup
    if (status === 'completed') {
      this.info.setCloseButtonVisible(false);
      await waitFor(250);
      await this.gotoMilestone(this.goal);
      await waitFor(1650);
      this.info.hide();
      this.showCelebration();
      await waitFor(750);
      this.showCongratulations(this.goal);
      sounds.sfx.play('attack-win');
      await waitFor(4000);
      this.hideCelebration();
      this.hideCongratulations();
      await openStreakBattleRoyaleReward();
      this.close();
      return;
    }

    // Show expired/failed animation and show the retry popup
    if (expired || status === 'failed') {
      this.info.setCloseButtonVisible(false);
      await waitFor(250);

      const opts = this.getAttackOpts(milestone);

      await this.cloud!.attack({
        ...opts,
        onAttacked: () => {
          opts.platform?.destroy();
          this.players.disqualifyAll();
          this.players.eliminatePlayers(this.getMilestonePosition(milestone));
        },
      });

      sounds.sfx.play('attack-lose');

      const success = await openStreakBattleRoyaleRetry();

      /**
       * Reset the event regardless of the outcome.
       * Hard reset will be triggered if the event has already expired.
       */
      await StateObserver.invoke.resetStreakBattleRoyale();

      // Only re-join event if the retry was successful and the event is not expired
      if (success && !expired) {
        // Re-join event
        await StateObserver.invoke.joinStreakBattleRoyale();
        // Re-run fake matchmaking
        await openStreakBattleRoyaleMatchmaking();
        // Reset the popup state
        this.setMilestone(getStreakBattleRoyaleLastMilestoneSeen(user()));
        this.info.setCloseButtonVisible(true);
        // Briefly wait before enabling the full screen continue button
        await waitFor(250);
        new StreakTapToContinue({
          superview: this.panel,
          textOpts: {
            font: 'Title',
            zIndex: 103,
          },
          tracking: {
            feature: Feature.MATCH2.STREAK_EVENT._,
            $subFeature: Feature.MATCH2.STREAK_EVENT.POPUP_CONTINUE,
          },
          onClick: () => this.close(),
        });
      } else {
        this.close();
      }

      return;
    }

    // Animate milestone progression for ongoing event
    if (status === 'active') {
      this.info.setCloseButtonVisible(true);
      const cached = getStreakBattleRoyaleLastMilestoneSeen(state);
      if (milestone > cached) {
        await waitFor(250);
        // Don't wait for the fall animations before taking off from checkpoint 0 to 1
        await this.gotoMilestone(milestone);
        await waitFor(1300);
        await this.showCongratulations(milestone);
        await waitFor(250);
        new StreakTapToContinue({
          superview: this.panel,
          textOpts: {
            font: 'Title',
            zIndex: 103,
          },
          tracking: {
            feature: Feature.MATCH2.STREAK_EVENT._,
            $subFeature: Feature.MATCH2.STREAK_EVENT.POPUP_CONTINUE,
          },
          onClick: () => this.close(),
        });
      } else this.showPlayButton();
      return;
    }
  }

  public setMilestone(milestone: number) {
    const sortedNewPlayers = this.getSurvivorsList(milestone);

    this.players.setupPlayers(sortedNewPlayers);
    this.movePlayersToMilestone(milestone, true);

    this.current = milestone;
    this.info.setCheckpoint(milestone);
    this.info.setSurvivors(sortedNewPlayers.length);

    this.updatePlatforms(true);

    StateObserver.invoke.setStreakBattleRoyaleLastMilestoneSeen(milestone);
  }

  public async gotoMilestone(milestone: number) {
    const newPlayers = getStreakBattleRoyalePlayersAtMilestone(milestone);

    this.current = milestone;
    this.players.updatePlayers(newPlayers);
    this.info.setSurvivors(newPlayers.length, true);

    this.updatePlatforms();

    if (milestone > 0) {
      this.repositionEliminatedPlayersToMilestone(milestone - 1);
    }

    await this.movePlayersToMilestone(milestone);

    if (milestone > 0) {
      const opts = this.getAttackOpts(milestone - 1);

      this.cloud!.attack({
        ...opts,
        onAttacked: () => {
          this.players.eliminatePlayers(
            this.getMilestonePosition(milestone - 1),
          );
          opts.platform?.destroy();
        },
      }).then(() => {
        if (milestone === this.goal) {
          this.cloud!.surrender();
        }
      });
    }

    this.info.setCheckpoint(milestone, true);

    await StateObserver.invoke.setStreakBattleRoyaleLastMilestoneSeen(
      milestone,
    );
  }

  private updatePlatforms(instant = false) {
    this.platforms?.platforms.forEach((platform, i) => {
      if (i >= this.current - (instant ? 1 : 2)) {
        platform.idle(this.current * 0.15);
      } else if (instant || i !== this.current) {
        platform.destroy(instant);
      }
    });
  }

  private async repositionEliminatedPlayersToMilestone(
    milestone: number,
    instant = false,
  ) {
    await this.players.repositionEliminatedPlayers({
      ...this.getMilestonePosition(milestone),
      milestone,
      instant,
    });
  }

  private async movePlayersToMilestone(milestone: number, instant = false) {
    await this.players.jumpTo({
      platform:
        milestone > 0 ? this.platforms!.getPlatform(milestone - 1) : undefined,
      ...this.getMilestonePosition(milestone),
      milestone,
      instant,
    });
  }

  private getAttackOpts(milestone: number) {
    let platform =
      milestone > 0 ? this.platforms!.getPlatform(milestone - 1) : undefined;
    const x =
      platform?.style.x ?? this.shrine.style.x + this.shrine.style.width / 2;
    const y = platform?.style.y ?? this.shrine.style.y + 215;
    const scale = platform ? platform.style.scale * 1.25 : 0.9;

    return { x, y, scale, platform };
  }

  private getMilestonePosition(milestone: number) {
    if (milestone > 0) {
      const { x, y } = this.platforms!.getPlatform(milestone - 1)!.style;
      return { x, y: y - 80 };
    }

    const { x, y, width } = this.shrine.style;
    return { x: x + width / 2, y: y + 195 };
  }

  public getSurvivorsList(currentMilestone: number) {
    const players = getStreakBattleRoyalePlayersAtMilestone(currentMilestone);
    const playersAtNextMilestoneSet = new Set(
      getStreakBattleRoyalePlayersAtMilestone(currentMilestone + 1).map(
        (player) => player.id,
      ),
    );

    const userPlayer = players[0];
    const eliminatedPlayers: { id: string; photo?: string }[] = [];
    const remainingPlayers: { id: string; photo?: string }[] = [];

    // Iterate through the rest of the players and sort into eliminated and remaining players
    for (let i = 1; i < players.length; i++) {
      const player = players[i];
      if (playersAtNextMilestoneSet.has(player.id)) {
        remainingPlayers.push(player);
      } else {
        eliminatedPlayers.push(player);
      }
    }

    // Sort the remaining players to place the eliminated players first
    return [userPlayer, ...eliminatedPlayers, ...remainingPlayers];
  }

  public async showCongratulations(milestone: number) {
    this.hideCongratulations();

    if (milestone === 0) return;

    if (milestone === this.goal) {
      await this.showYouMadeIt();
      return;
    }

    await this.message.showMessage(() => {
      let suffix = '';

      // Add suffix to the milestone number for English
      if (StateObserver.getLocale() === 'en') {
        switch (milestone) {
          case 1:
            suffix = 'st';
            break;
          case 2:
            suffix = 'nd';
            break;
          case 3:
            suffix = 'rd';
            break;
          default:
            suffix = 'th';
            break;
        }
      }

      return i18n('match2.streakBattleRoyale.popup.messageV2', {
        level: `${milestone}${suffix}`,
      });
    });
  }

  public async showYouMadeIt(instant = false) {
    this.youMadeItText.updateOpts({ visible: true });
    await gsap.to(this.youMadeItText.style, {
      duration: instant ? Number.EPSILON : 0.8,
      scale: 1,
      ease: 'elastic.out',
      overwrite: true,
    });
  }

  public async showPlayButton(instant = false) {
    this.playButton.updateOpts({ visible: true });
    await gsap.to(this.playButton.style, {
      duration: instant ? Number.EPSILON : 0.3,
      opacity: 1,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public showCelebration() {
    const offsetY = 150;

    this.firework = new Fireworks({
      superview: this.panel,
      width: this.panel.style.width,
      height: this.panel.style.height + offsetY,
      offsetX: -this.panel.style.width * 0.5,
      offsetY: -this.panel.style.height * 0.5 - offsetY,
    });

    this.firework.startFireworks();
  }

  public async hideCelebration() {
    this.firework?.stopFireworks();
  }

  public async hideCongratulations() {
    await Promise.all([this.hideYouMadeIt(), this.message.hideMessage()]);
  }

  public async hideYouMadeIt(instant = false) {
    await gsap.to(this.youMadeItText.style, {
      duration: instant ? Number.EPSILON : 0.3,
      scale: 0,
      ease: 'back.in',
      overwrite: true,
    });
    this.youMadeItText.updateOpts({ visible: false });
  }

  public async hidePlayButton(instant = false) {
    if (!instant) {
      // This promise does not complete if instant (duration = 0)
      await gsap.to(this.playButton.style, {
        duration: 0.3,
        opacity: 0,
        ease: 'quad.inOut',
        overwrite: true,
      });
    }
    this.playButton.updateOpts({ visible: false, opacity: 0 });
  }

  public override async fadeIn() {
    await super.fadeIn();
    this.handleStateUpdate();
  }

  public override async fadeOut() {
    this.hidePlayButton();
    await super.fadeOut();
    this.chest.stopAnimation();
    this.hideCelebration();
  }

  protected getAssetsGroup() {
    const urls = getStreakBattleRoyaleAssetPaths();
    return new AssetGroup([
      ...urls,
      streakBgAssets,
      streakCloudMascotAssets,
      streakCloudPlatformAssets,
    ]);
  }

  protected async postLoadingInit() {
    const bg = new SpineAnimationView({
      superview: this.panel,
      spinePath: streakBgAssets.url,
      zIndex: -3,
      x: 385,
      y: 580,
      scale: 0.68,
    });
    bg.start({ id: 'idle', loop: true });
    this.platforms = new StreakCloudPlatforms(this.panel);
    this.cloud = new StreakCloudMascot(this.panel);
    this.setMilestone(getStreakBattleRoyaleLastMilestoneSeen(user()));
  }
}
